import React, { useEffect, useState } from "react";
import Modal1 from "../../../../components/DIsplay/Modal/Modal1";
import Icon from "../../../../components/HOC/Icon";
import Button1 from "../../../../components/form/Button1";
import { Switch } from "@mui/material";
import CommonCollapsible from "../../../../components/common/CommonCollapsible";

function OverrideMarkupCard({
  markup,
  onEdit,
  onDelete,
  onToggleStatus,
  statusLoading,
  collapsible = false,
  expanded = true,
  onToggleExpand,
}) {
  const isActive = markup?.status === "Active";
  const suppliers = (markup?.suppliers || []).join(", ") || "All suppliers";
  const fareType = Array.isArray(markup?.fareType)
    ? markup.fareType.join(", ")
    : markup?.fareType || "All fare types";

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <button
        type="button"
        onClick={collapsible ? onToggleExpand : undefined}
        className={`flex w-full items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 text-left ${
          collapsible ? "cursor-pointer hover:bg-slate-50/80 transition" : ""
        }`}
        aria-expanded={collapsible ? expanded : undefined}
      >
        <div className="min-w-0 flex-1">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium uppercase tracking-wide text-slate-600">
            <Icon icon="mdi:shield-star-outline" className="text-sm" />
            Override rule
          </div>
          <h3 className="truncate text-lg font-semibold text-slate-900">
            {markup?.name}
          </h3>
          {expanded || !collapsible ? (
            <span className="mt-1 block text-sm text-slate-500">
              Takes priority over standard flight markups
            </span>
          ) : (
            <span className="mt-1 block truncate text-sm text-slate-500">
              {markup?.type} · {markup?.amount ?? 0} ({markup?.method || "—"})
              {suppliers !== "All suppliers" ? ` · ${suppliers}` : ""}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span
            className={`rounded-full px-3 py-1 text-xs font-semibold ${
              isActive
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                : "bg-slate-100 text-slate-600 ring-1 ring-slate-200"
            }`}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
          {collapsible ? (
            <Icon
              icon="mdi:chevron-down"
              className={`text-xl text-slate-400 transition-transform duration-200 ${
                expanded ? "rotate-180" : ""
              }`}
            />
          ) : null}
        </div>
      </button>

      <CommonCollapsible open={!collapsible || expanded} duration={200}>
        <div className="grid gap-3 p-5 sm:grid-cols-2">
          {[
            { label: "Type", value: markup?.type, icon: "mdi:tag-outline" },
            {
              label: "Amount",
              value: `${markup?.amount ?? 0} (${markup?.method || "—"})`,
              icon: "mdi:cash-multiple",
            },
            { label: "Suppliers", value: suppliers, icon: "mdi:airplane" },
            {
              label: "Fare type",
              value: fareType,
              icon: "mdi:ticket-confirmation-outline",
            },
            {
              label: "Price type",
              value: markup?.priceType || "Any",
              icon: "mdi:currency-usd",
            },
            {
              label: "Status",
              value: markup?.status,
              icon: "mdi:traffic-light",
            },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-xl border border-slate-100 bg-slate-50/80 p-3"
            >
              <div className="mb-1 flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                <Icon icon={item.icon} className="text-sm" />
                {item.label}
              </div>
              <span className="block text-sm font-semibold text-slate-800">
                {item.value}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/60 px-5 py-4">
          <div className="flex items-center gap-3">
            <Switch
              checked={isActive}
              disabled={statusLoading}
              onChange={(ev) => onToggleStatus(ev.target.checked, markup)}
              onClick={(ev) => ev.stopPropagation()}
            />
            <span className="text-sm font-medium text-slate-700">
              {isActive ? "Enabled" : "Disabled"}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onEdit(markup)}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
            >
              <Icon icon="tabler:edit" />
              Edit
            </button>
            <button
              type="button"
              onClick={() => onDelete(markup)}
              className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-600 transition hover:bg-red-100"
            >
              <Icon icon="material-symbols-light:delete" />
              Delete
            </button>
          </div>
        </div>
      </CommonCollapsible>
    </div>
  );
}

export default function OverrideMarkupModal({
  open,
  onClose,
  markups = [],
  loading,
  onEdit,
  onDelete,
  onToggleStatus,
  statusLoadingId,
  onCreate,
}) {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const activeCount = markups.filter((m) => m.status === "Active").length;
  const isCollapsible = markups.length > 1;

  useEffect(() => {
    if (!open) {
      setExpandedIds(new Set());
      return;
    }
    if (markups.length === 1) {
      setExpandedIds(new Set([markups[0]?._id].filter(Boolean)));
      return;
    }
    if (markups.length > 1) {
      setExpandedIds(new Set([markups[0]?._id].filter(Boolean)));
    }
  }, [open, markups]);

  function toggleExpanded(id) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleClose() {
    setConfirmDelete(null);
    onClose?.();
  }

  return (
    <Modal1
      open={open}
      setOpen={(val) => !val && handleClose()}
      modalBorderRadius="rounded-2xl"
    >
      <div className="w-[min(100vw-2rem,560px)] overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Override Markups
            </h2>
            <span className="block text-sm text-slate-500">
              {markups.length
                ? `${markups.length} override rule${markups.length === 1 ? "" : "s"}${activeCount ? ` · ${activeCount} active` : ""}`
                : "Create override rules that take priority over standard flight markups"}
            </span>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
          >
            <Icon icon="mdi:close" />
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500">
              <div className="primaryLoader" />
              <span className="text-sm">Loading override markups...</span>
            </div>
          ) : markups?.length ? (
            <div className="flex flex-col gap-4">
              {markups.map((markup) =>
                confirmDelete?._id === markup?._id ? (
                  <div
                    key={markup._id}
                    className="rounded-2xl border border-red-200 bg-red-50 p-5"
                  >
                    <div className="mb-4 flex items-start gap-3">
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600">
                        <Icon icon="mdi:alert-circle-outline" />
                      </span>
                      <div>
                        <h4 className="font-semibold text-red-900">
                          Delete override markup?
                        </h4>
                        <span className="mt-1 block text-sm text-red-700/80">
                          <strong>{markup?.name}</strong> will be permanently
                          removed. This cannot be undone.
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button1
                        className="btn-theme-light"
                        onClick={() => setConfirmDelete(null)}
                      >
                        Cancel
                      </Button1>
                      <Button1
                        className="!bg-red-500 !text-white"
                        onClick={() => {
                          onDelete(markup);
                          setConfirmDelete(null);
                        }}
                      >
                        Delete markup
                      </Button1>
                    </div>
                  </div>
                ) : (
                  <OverrideMarkupCard
                    key={markup._id}
                    markup={markup}
                    onEdit={onEdit}
                    onDelete={setConfirmDelete}
                    onToggleStatus={onToggleStatus}
                    statusLoading={statusLoadingId === markup?._id}
                    collapsible={isCollapsible}
                    expanded={expandedIds.has(markup._id)}
                    onToggleExpand={() => toggleExpanded(markup._id)}
                  />
                ),
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-6 py-12 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-600">
                <Icon icon="mdi:shield-plus-outline" className="text-2xl" />
              </span>
              <div>
                <h3 className="font-semibold text-slate-900">
                  No override markups yet
                </h3>
                <span className="mt-1 block text-sm text-slate-500">
                  Create override rules that take priority over standard flight
                  markup rules.
                </span>
              </div>
              <Button1 onClick={onCreate}>Create override markup</Button1>
            </div>
          )}
        </div>

        {!loading && markups.length > 0 ? (
          <div className="border-t border-slate-100 bg-slate-50/80 px-6 py-4">
            <Button1 className="w-full !rounded-xl" onClick={onCreate}>
              <span className="inline-flex items-center justify-center gap-2">
                <Icon icon="mdi:plus" />
                Create another override markup
              </span>
            </Button1>
          </div>
        ) : null}
      </div>
    </Modal1>
  );
}
